import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/frontend/components/ui/card";
import { Button } from "@/frontend/components/ui/button";
import { Input } from "@/frontend/components/ui/input";
import { Label } from "@/frontend/components/ui/label";
import { Textarea } from "@/frontend/components/ui/textarea";
import { Switch } from "@/frontend/components/ui/switch";
import { Checkbox } from "@/frontend/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/frontend/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/frontend/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/frontend/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/frontend/components/ui/alert-dialog";
import { Alert, AlertDescription } from "@/frontend/components/ui/alert";
import { Badge } from "@/frontend/components/ui/badge";
import { StatusBadge } from "@/frontend/components/admin/StatusBadge";
import { Plus, Search, Pencil, Trash2, Loader2, AlertCircle, X, FileText, Home } from "lucide-react";
import { RoomTypeFormFields, type RoomTypeFieldsData } from "@/frontend/components/admin/RoomTypeFormFields";
import { ImageUploadField } from "@/frontend/components/admin/ImageUploadField";
import { CommonAreasFields } from "@/frontend/components/admin/CommonAreasFields";
import { useConfig } from "@/backend/hooks/useConfig";
import { Residence, RoomType, CommonArea } from "@/shared/types";
import { useToast } from "@/backend/hooks/use-toast";
import { useAdminResidences, useCreateResidence, useUpdateResidence, useDeleteResidence } from "@/backend/hooks/admin/useAdminResidences";
import { useAdminRoomTypes, useCreateRoomType, useUpdateRoomType, useDeleteRoomType, useDeleteRoomTypesSafely } from "@/backend/hooks/admin/useAdminRoomTypes";
import { calculateMinimumPrice, isDraft, pricedPublishedRooms } from "@/backend/lib/residenceUtils";
import { validateResidenceDraft, validateResidencePublish, validateRoomDraft, validateRoomPublish } from "@/backend/lib/adminValidation";

// Temporary room type for form management
interface TempRoomType {
  tempId: string; // Temporary ID for local state management
  id?: string; // Real ID if it exists in DB
  name: string;
  description: string;
  priceByOccupancy: number[];
  maxOccupancy: number;
  area: number;
  floorPlanUrl: string;
  imagesUrl: string[];
  minStay: number;
  pricePerExcessKwh?: number;
  typologyKey?: string;
  // Explicit per-room publication state, chosen via Save draft / Publish in the
  // room dialog — the same model as the dedicated Rooms page. Absent === published.
  status: "draft" | "published";
  // A typology row represents N identical units collapsed into one entry.
  // memberIds are the real Firestore doc ids of the existing units (empty for
  // not-yet-saved rooms). unitCount is the desired total; save reconciles the
  // difference by creating/deleting sibling docs. Standalone rooms: count 1.
  unitCount: number;
  memberIds: string[];
}

// True when an outside-interaction originated from a portaled Radix popper
// (Select dropdown, combobox) rather than the modal overlay — used to avoid
// closing the editor when the admin picks an option from such a dropdown.
const clickIsInsidePopper = (target: EventTarget | null): boolean => {
  const el = target as HTMLElement | null;
  return !!el?.closest?.('[data-radix-popper-content-wrapper],[role="listbox"]');
};

// Stable snapshot of a single room's fields, to detect whether the room dialog
// changed anything (so a no-op Save draft / Publish can be disabled). `status` is
// omitted — it's chosen by the button, not edited in the form.
const serializeRoomFields = (rt: Partial<TempRoomType>): string =>
  JSON.stringify({
    name: rt.name ?? "",
    description: rt.description ?? "",
    priceByOccupancy: rt.priceByOccupancy ?? [],
    maxOccupancy: rt.maxOccupancy ?? 1,
    area: rt.area ?? 0,
    floorPlanUrl: rt.floorPlanUrl ?? "",
    imagesUrl: (rt.imagesUrl ?? []).filter((u) => u.trim() !== ""),
    minStay: rt.minStay ?? 1,
    pricePerExcessKwh: rt.pricePerExcessKwh ?? null,
    typologyKey: rt.typologyKey ?? "",
  });

// Stable, order-independent snapshot of the residence form (fields + common areas
// + rooms). Used to detect whether anything actually changed, so a no-op Save can
// be disabled. Normalisation mirrors what `handleSave` writes (trimmed common
// areas, blank image URLs stripped) so cosmetic-only edits don't read as dirty.
const serializeResidenceForm = (
  res: Partial<Residence>,
  rooms: TempRoomType[],
  commonAreas: CommonArea[],
): string =>
  JSON.stringify({
    name: res.name ?? "",
    city: res.city ?? "",
    address: res.address ?? "",
    description: res.description ?? "",
    fullDescription: res.fullDescription ?? "",
    imageUrl: res.imageUrl ?? "",
    amenities: [...(res.amenities ?? [])].sort(),
    active: res.active ?? true,
    commonAreas: (commonAreas ?? []).map((a) => ({
      name: (a.name ?? "").trim(),
      description: (a.description ?? "").trim(),
      imagesUrl: (a.imagesUrl ?? []).filter((u) => u.trim() !== ""),
    })),
    rooms: [...rooms]
      .sort((a, b) => a.tempId.localeCompare(b.tempId))
      .map((rt) => ({
        name: rt.name ?? "",
        description: rt.description ?? "",
        priceByOccupancy: rt.priceByOccupancy ?? [],
        maxOccupancy: rt.maxOccupancy ?? 1,
        area: rt.area ?? 0,
        floorPlanUrl: rt.floorPlanUrl ?? "",
        imagesUrl: (rt.imagesUrl ?? []).filter((u) => u.trim() !== ""),
        minStay: rt.minStay ?? 1,
        pricePerExcessKwh: rt.pricePerExcessKwh ?? null,
        typologyKey: rt.typologyKey ?? "",
        status: rt.status ?? "published",
        unitCount: rt.typologyKey ? rt.unitCount ?? 1 : 1,
      })),
  });

const AdminResidences = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: residences = [], isLoading: residencesLoading } = useAdminResidences();
  const { data: allRoomTypes = [], isLoading: roomTypesLoading } = useAdminRoomTypes();
  const { data: config, isLoading: configLoading } = useConfig();
  const createResidence = useCreateResidence();
  const updateResidence = useUpdateResidence();
  const deleteResidence = useDeleteResidence();
  const createRoomType = useCreateRoomType();
  const updateRoomType = useUpdateRoomType();
  const deleteRoomType = useDeleteRoomType();
  const deleteRoomTypesSafely = useDeleteRoomTypesSafely();

  const isLoading = residencesLoading || configLoading;

  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  // Confirm prompt shown when the user tries to close the editor with unsaved edits.
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
  // Same, for the inner Add/Edit Room dialog.
  const [isRoomDiscardDialogOpen, setIsRoomDiscardDialogOpen] = useState(false);
  const [isRoomTypeDialogOpen, setIsRoomTypeDialogOpen] = useState(false);
  const [selectedResidence, setSelectedResidence] = useState<Residence | null>(null);
  const [formData, setFormData] = useState<Partial<Residence>>({
    name: "",
    city: "",
    address: "",
    description: "",
    fullDescription: "",
    imageUrl: "",
    amenities: [],
    active: true,
    startingPrice: null,
    commonAreas: [],
  });

  // Room types management
  const [roomTypes, setRoomTypes] = useState<TempRoomType[]>([]);
  // Real IDs of rooms the user explicitly removed in this modal session. Saving
  // deletes exactly these — never rooms merely absent from the draft — so an
  // incompletely-hydrated draft can never silently wipe existing rooms.
  const [removedRoomTypeIds, setRemovedRoomTypeIds] = useState<string[]>([]);
  const [roomTypeFormData, setRoomTypeFormData] = useState<Partial<TempRoomType>>({
    name: "",
    description: "",
    priceByOccupancy: [0],
    maxOccupancy: 1,
    area: 0,
    floorPlanUrl: "",
    imagesUrl: [],
    minStay: 1,
    pricePerExcessKwh: undefined,
  });
  const [editingRoomTypeId, setEditingRoomTypeId] = useState<string | null>(null);
  const [isImageUploading, setIsImageUploading] = useState(false);
  // Which save action is in flight, so only the clicked button shows a spinner.
  const [savingAction, setSavingAction] = useState<"draft" | "publish" | null>(null);
  // Snapshot of the form as loaded, to detect unsaved changes (null for a new
  // residence → always treated as dirty).
  const initialSnapshotRef = useRef<string | null>(null);
  // Snapshot of the inner room dialog as opened, to detect edits before closing it.
  const roomDialogInitialRef = useRef<string | null>(null);
  const [newResidenceId, setNewResidenceId] = useState<string>("");
  const [newRoomTypeId, setNewRoomTypeId] = useState<string>("");
  // Desired unit count for the room currently open in the dialog (typology only).
  const [roomFormUnitCount, setRoomFormUnitCount] = useState<number>(1);

  // Hydrate the rooms list from server data exactly once per modal session.
  // Without this guard, every background refetch of ['admin', 'roomTypes']
  // (triggered by each per-room mutation in handleSave) would overwrite the
  // user's in-progress local edits and reorder the list alphabetically.
  const hydratedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isModalOpen) {
      hydratedForRef.current = null;
      initialSnapshotRef.current = null;
      return;
    }
    if (!selectedResidence) return;
    // Wait until the room-types query has actually loaded before hydrating.
    // Otherwise an edit modal opened during the initial fetch hydrates from an
    // empty list, the guard below blocks re-hydration once data arrives, and
    // saving then deletes every existing room as "removed". (Data-loss race.)
    if (roomTypesLoading) return;
    if (hydratedForRef.current === selectedResidence.id) return;
    hydratedForRef.current = selectedResidence.id;

    const sorted = allRoomTypes
      .filter((rt) => rt.residenceId === selectedResidence.id)
      .slice()
      .sort((a, b) => (a.createdAt?.toMillis() ?? 0) - (b.createdAt?.toMillis() ?? 0));

    // Collapse each typology's N unit docs into a single row (count + memberIds);
    // standalone rooms (no typologyKey) stay as their own count-1 row. The first
    // doc in creation order is the representative whose fields we edit.
    const seenTypologies = new Set<string>();
    const residenceRoomTypes: TempRoomType[] = [];
    for (const rt of sorted) {
      if (rt.typologyKey) {
        if (seenTypologies.has(rt.typologyKey)) continue;
        seenTypologies.add(rt.typologyKey);
        const members = sorted.filter((r) => r.typologyKey === rt.typologyKey);
        residenceRoomTypes.push({
          tempId: rt.id,
          id: rt.id,
          name: rt.name,
          description: rt.description,
          priceByOccupancy: rt.priceByOccupancy ?? [],
          maxOccupancy: rt.maxOccupancy,
          area: rt.area,
          floorPlanUrl: rt.floorPlanUrl,
          imagesUrl: rt.imagesUrl || [],
          minStay: rt.minStay || 1,
          pricePerExcessKwh: rt.pricePerExcessKwh,
          typologyKey: rt.typologyKey,
          status: rt.status ?? "published",
          unitCount: members.length,
          memberIds: members.map((m) => m.id),
        });
      } else {
        residenceRoomTypes.push({
          tempId: rt.id,
          id: rt.id,
          name: rt.name,
          description: rt.description,
          priceByOccupancy: rt.priceByOccupancy ?? [],
          maxOccupancy: rt.maxOccupancy,
          area: rt.area,
          floorPlanUrl: rt.floorPlanUrl,
          imagesUrl: rt.imagesUrl || [],
          minStay: rt.minStay || 1,
          pricePerExcessKwh: rt.pricePerExcessKwh,
          typologyKey: rt.typologyKey,
          status: rt.status ?? "published",
          unitCount: 1,
          memberIds: [rt.id],
        });
      }
    }
    setRoomTypes(residenceRoomTypes);
    // Capture the loaded state as the baseline for dirty-detection.
    initialSnapshotRef.current = serializeResidenceForm(
      selectedResidence,
      residenceRoomTypes,
      selectedResidence.commonAreas ?? [],
    );
  }, [selectedResidence, isModalOpen, allRoomTypes, roomTypesLoading]);

  const filteredResidences = residences.filter(
    (r) =>
      r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.city.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const openAddModal = () => {
    setSelectedResidence(null);
    setNewResidenceId(crypto.randomUUID());
    setFormData({
      name: "",
      city: "",
      address: "",
      description: "",
      fullDescription: "",
      imageUrl: "",
      amenities: [],
      active: true,
      startingPrice: null,
      commonAreas: [],
    });
    setRoomTypes([]);
    setRemovedRoomTypeIds([]);
    setRoomTypeFormData({
      name: "",
      description: "",
      priceByOccupancy: [0],
      maxOccupancy: 1,
      area: 0,
      floorPlanUrl: "",
      imagesUrl: [],
      minStay: 1,
      pricePerExcessKwh: undefined,
    });
    setEditingRoomTypeId(null);
    setIsModalOpen(true);
  };

  const openEditModal = (residence: Residence) => {
    setSelectedResidence(residence);
    setRemovedRoomTypeIds([]);
    setFormData({ ...residence });
    setRoomTypeFormData({
      name: "",
      description: "",
      priceByOccupancy: [0],
      maxOccupancy: 1,
      area: 0,
      floorPlanUrl: "",
      imagesUrl: [],
      pricePerExcessKwh: undefined,
    });
    setEditingRoomTypeId(null);
    setIsModalOpen(true);
  };

  const openDeleteDialog = (residence: Residence) => {
    setSelectedResidence(residence);
    setIsDeleteDialogOpen(true);
  };

  const emptyRoomForm: Partial<TempRoomType> = {
    name: "",
    description: "",
    priceByOccupancy: [0],
    maxOccupancy: 1,
    area: 0,
    floorPlanUrl: "",
    imagesUrl: [],
    minStay: 1,
    pricePerExcessKwh: undefined,
  };

  const openRoomTypeDialog = (roomType?: TempRoomType) => {
    if (roomType) {
      const initial: Partial<TempRoomType> = {
        name: roomType.name,
        description: roomType.description,
        priceByOccupancy: roomType.priceByOccupancy,
        maxOccupancy: roomType.maxOccupancy,
        area: roomType.area,
        floorPlanUrl: roomType.floorPlanUrl,
        imagesUrl: roomType.imagesUrl || [],
        minStay: roomType.minStay || 1,
        pricePerExcessKwh: roomType.pricePerExcessKwh,
        typologyKey: roomType.typologyKey,
        status: roomType.status,
      };
      setRoomTypeFormData(initial);
      setRoomFormUnitCount(roomType.unitCount ?? 1);
      roomDialogInitialRef.current = serializeRoomFields(initial);
      setEditingRoomTypeId(roomType.tempId);
    } else {
      const generatedId = crypto.randomUUID();
      setNewRoomTypeId(generatedId);
      setRoomTypeFormData(emptyRoomForm);
      setRoomFormUnitCount(1);
      roomDialogInitialRef.current = serializeRoomFields(emptyRoomForm);
      setEditingRoomTypeId(null);
    }
    setIsRoomTypeDialogOpen(true);
  };

  // Close + reset the inner room dialog (used by Cancel and the discard confirm).
  const closeRoomTypeDialog = () => {
    setIsRoomTypeDialogOpen(false);
    setRoomTypeFormData(emptyRoomForm);
    setEditingRoomTypeId(null);
    roomDialogInitialRef.current = null;
  };

  const handleSaveRoomType = (publish: boolean) => {
    // Same explicit model as the dedicated Rooms page: a draft needs only a name,
    // publishing runs the full validation. The chosen state is stored on the room.
    const residenceId = selectedResidence?.id ?? newResidenceId;
    const error = publish
      ? validateRoomPublish(roomTypeFormData)
      : validateRoomDraft({ residenceId, name: roomTypeFormData.name });
    if (error) {
      toast({ title: "Error", description: error, variant: "destructive" });
      return;
    }

    const status: "draft" | "published" = publish ? "published" : "draft";

    // Strip blank image URLs; keep whatever the admin has entered so far.
    const filteredImages = (roomTypeFormData.imagesUrl || []).filter((url) => url.trim() !== "");

    if (editingRoomTypeId) {
      // Update existing
      setRoomTypes((prev) =>
        prev.map((rt) =>
          rt.tempId === editingRoomTypeId
            ? {
                ...rt,
                name: roomTypeFormData.name!,
                description: roomTypeFormData.description ?? "",
                priceByOccupancy: roomTypeFormData.priceByOccupancy ?? [0],
                maxOccupancy: roomTypeFormData.maxOccupancy ?? 1,
                area: roomTypeFormData.area ?? 0,
                floorPlanUrl: roomTypeFormData.floorPlanUrl ?? "",
                imagesUrl: filteredImages,
                minStay: roomTypeFormData.minStay ?? 1,
                pricePerExcessKwh: roomTypeFormData.pricePerExcessKwh,
                typologyKey: roomTypeFormData.typologyKey,
                status,
                unitCount: roomTypeFormData.typologyKey ? Math.max(1, roomFormUnitCount) : 1,
              }
            : rt
        )
      );
      setEditingRoomTypeId(null);
    } else {
      // Add new (use pre-generated ID so uploaded images already reference this ID)
      const newRoomType: TempRoomType = {
        tempId: newRoomTypeId,
        name: roomTypeFormData.name!,
        description: roomTypeFormData.description ?? "",
        priceByOccupancy: roomTypeFormData.priceByOccupancy ?? [0],
        maxOccupancy: roomTypeFormData.maxOccupancy ?? 1,
        area: roomTypeFormData.area ?? 0,
        floorPlanUrl: roomTypeFormData.floorPlanUrl ?? "",
        imagesUrl: filteredImages,
        minStay: roomTypeFormData.minStay ?? 1,
        pricePerExcessKwh: roomTypeFormData.pricePerExcessKwh,
        typologyKey: roomTypeFormData.typologyKey,
        status,
        unitCount: roomTypeFormData.typologyKey ? Math.max(1, roomFormUnitCount) : 1,
        memberIds: [],
      };
      setRoomTypes((prev) => [...prev, newRoomType]);
    }

    // Reset form and close dialog
    setRoomTypeFormData({
      name: "",
      description: "",
      priceByOccupancy: [0],
      maxOccupancy: 1,
      area: 0,
      floorPlanUrl: "",
      imagesUrl: [],
      minStay: 1,
      pricePerExcessKwh: undefined,
    });
    setIsRoomTypeDialogOpen(false);
  };

  const handleDeleteRoomType = (tempId: string) => {
    // Remember the real IDs so save deletes exactly the rooms the user removed.
    // A typology row stands for all its member unit docs — remove every one.
    const removed = roomTypes.find((rt) => rt.tempId === tempId);
    const idsToRemove = (removed?.memberIds?.length ? removed.memberIds : removed?.id ? [removed.id] : []);
    if (idsToRemove.length) {
      setRemovedRoomTypeIds((prev) => Array.from(new Set([...prev, ...idsToRemove])));
    }
    setRoomTypes((prev) => prev.filter((rt) => rt.tempId !== tempId));
    if (editingRoomTypeId === tempId) {
      setEditingRoomTypeId(null);
      setRoomTypeFormData({
        name: "",
        description: "",
        priceByOccupancy: [0],
        maxOccupancy: 1,
        area: 0,
        floorPlanUrl: "",
        imagesUrl: [],
        minStay: 1,
        pricePerExcessKwh: undefined,
      });
    }
  };

  const handleSave = async (publish: boolean) => {
    // Residence-level validation: a draft only needs a name; publishing needs the
    // full descriptive fields plus complete common areas.
    const residenceError = publish
      ? validateResidencePublish(formData, formData.commonAreas ?? [])
      : validateResidenceDraft(formData);
    if (residenceError) {
      toast({ title: "Error", description: residenceError, variant: "destructive" });
      return;
    }

    // Each room already carries the explicit status the admin chose in the room
    // dialog (Save draft / Publish) — drafts are kept, never dropped.
    const roomsWithStatus = roomTypes;

    // Publishing requires at least one published room so the public detail page
    // is never empty. Draft rooms still save alongside it, hidden.
    if (publish && !roomsWithStatus.some((rt) => rt.status === "published")) {
      toast({
        title: "Error",
        description: "Add at least one complete room before publishing.",
        variant: "destructive",
      });
      return;
    }

    const status: "draft" | "published" = publish ? "published" : "draft";

    // Normalise common areas: trim names, default description to "" (Firestore
    // rejects undefined), and strip any blank image URLs.
    const normalizedCommonAreas = (formData.commonAreas ?? []).map((area) => ({
      name: area.name.trim(),
      description: area.description?.trim() || "",
      imagesUrl: (area.imagesUrl || []).filter((url) => url.trim() !== ""),
    }));

    setSavingAction(publish ? "publish" : "draft");
    try {
      let residenceId: string;

      if (selectedResidence) {
        // Edit existing residence (exclude startingPrice as it's auto-calculated)
        const { startingPrice, ...residenceUpdateData } = formData;
        await updateResidence.mutateAsync({
          id: selectedResidence.id,
          ...residenceUpdateData,
          status,
          commonAreas: normalizedCommonAreas,
        });
        residenceId = selectedResidence.id;

        // Delete only the rooms the user explicitly removed in this session.
        // Diffing the draft against the DB would delete any room missing from a
        // partially-hydrated draft, silently wiping rooms the user never touched.
        const draftIds = new Set(roomsWithStatus.filter((rt) => rt.id).map((rt) => rt.id!));
        const deletedIds = removedRoomTypeIds.filter((id) => !draftIds.has(id));
        for (const id of deletedIds) {
          await deleteRoomType.mutateAsync(id);
        }

        // Update or create room types
        for (const rt of roomsWithStatus) {
          // Ensure imagesUrl is always an array, filter out any undefined/null values
          const imagesUrl = (rt.imagesUrl || []).filter((url): url is string => Boolean(url));

          const sharedFields = {
            name: rt.name,
            description: rt.description,
            priceByOccupancy: rt.priceByOccupancy,
            maxOccupancy: rt.maxOccupancy,
            area: rt.area,
            floorPlanUrl: rt.floorPlanUrl,
            imagesUrl: imagesUrl,
            minStay: rt.minStay || 1,
            pricePerExcessKwh: rt.pricePerExcessKwh,
            typologyKey: rt.typologyKey,
            status: rt.status,
          };
          const desiredUnits = rt.typologyKey ? Math.max(1, rt.unitCount ?? 1) : 1;

          if (rt.id) {
            // Update the representative doc; useUpdateRoomType propagates the
            // shared fields onto every typology sibling automatically.
            await updateRoomType.mutateAsync({ id: rt.id, residenceId, ...sharedFields });

            // Reconcile unit count against the existing member docs.
            const currentUnits = rt.memberIds.length || 1;
            const delta = desiredUnits - currentUnits;
            if (delta > 0) {
              await createRoomType.mutateAsync({
                residenceId,
                ...sharedFields,
                count: delta,
              } as Omit<RoomType, "id" | "createdAt" | "updatedAt"> & { count: number });
            } else if (delta < 0) {
              // Remove surplus units, newest first, skipping any with bookings.
              const removable = [...rt.memberIds].reverse().filter((id) => id !== rt.id);
              await deleteRoomTypesSafely.mutateAsync({
                candidateIds: removable,
                max: -delta,
                residenceId,
              });
            }
          } else {
            await createRoomType.mutateAsync({
              residenceId,
              ...sharedFields,
              count: desiredUnits,
            } as Omit<RoomType, "id" | "createdAt" | "updatedAt"> & { count: number });
          }
        }
      } else {
        // Create new residence with pre-generated ID (so uploaded images already reference this ID)
        const { startingPrice, ...residenceData } = formData;
        residenceId = await createResidence.mutateAsync({
          id: newResidenceId,
          ...residenceData,
          status,
          commonAreas: normalizedCommonAreas,
        } as Omit<Residence, "id" | "createdAt" | "updatedAt" | "startingPrice"> & { id: string; startingPrice: null });

        // Create all room types (using tempId as document ID so uploaded images match)
        for (const rt of roomsWithStatus) {
          // Ensure imagesUrl is always an array, filter out any undefined/null values
          const imagesUrl = (rt.imagesUrl || []).filter((url): url is string => Boolean(url));

          await createRoomType.mutateAsync({
            id: rt.tempId,
            residenceId,
            name: rt.name,
            description: rt.description,
            priceByOccupancy: rt.priceByOccupancy,
            maxOccupancy: rt.maxOccupancy,
            area: rt.area,
            floorPlanUrl: rt.floorPlanUrl,
            imagesUrl: imagesUrl,
            minStay: rt.minStay || 1,
            pricePerExcessKwh: rt.pricePerExcessKwh,
            typologyKey: rt.typologyKey,
            status: rt.status,
            // Materialize N identical units for a typology; the first uses
            // tempId (so uploaded images match), the rest get generated ids.
            count: rt.typologyKey ? Math.max(1, rt.unitCount ?? 1) : 1,
          } as Omit<RoomType, "createdAt" | "updatedAt"> & { id: string; count: number });
        }

        // Starting price reflects only publishable rooms with valid prices.
        const minPrice = calculateMinimumPrice(
          roomsWithStatus.filter((rt) => rt.status === "published")
        );
        await updateResidence.mutateAsync({
          id: residenceId,
          startingPrice: minPrice,
        });
      }

      const draftCount = roomsWithStatus.filter((rt) => rt.status === "draft").length;
      const base = publish ? "Residence published" : "Draft saved";
      const extra =
        publish && draftCount > 0
          ? ` · ${draftCount} draft room${draftCount > 1 ? "s" : ""} kept hidden`
          : "";
      toast({ title: "Success", description: base + extra });

      setIsModalOpen(false);
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to save residence. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSavingAction(null);
    }
  };

  const handleDelete = async () => {
    if (selectedResidence) {
      try {
        await deleteResidence.mutateAsync(selectedResidence.id);
        toast({ title: "Success", description: "Residence deleted successfully" });
        setIsDeleteDialogOpen(false);
      } catch (err) {
        toast({
          title: "Error",
          description: "Failed to delete residence. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  const toggleAmenity = (amenity: string) => {
    setFormData((prev) => ({
      ...prev,
      amenities: prev.amenities?.includes(amenity)
        ? prev.amenities.filter((a) => a !== amenity)
        : [...(prev.amenities || []), amenity],
    }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const isMutating =
    createResidence.isPending ||
    updateResidence.isPending ||
    createRoomType.isPending ||
    updateRoomType.isPending ||
    deleteRoomType.isPending ||
    isImageUploading ||
    savingAction !== null;

  // Has anything changed since the form was loaded? A new residence (no snapshot)
  // is always dirty. Drives both the Save-button disabling and the close confirm.
  const isDirty =
    initialSnapshotRef.current === null ||
    serializeResidenceForm(formData, roomTypes, formData.commonAreas ?? []) !==
      initialSnapshotRef.current;
  const currentStatus = selectedResidence?.status ?? "published";
  // Don't allow a no-op Publish on an already-published residence, nor a no-op
  // Save draft on an already-draft one. New residences and real edits stay enabled.
  const isPublishDisabled =
    isMutating ||
    roomTypes.length === 0 ||
    (!!selectedResidence && currentStatus === "published" && !isDirty);
  const isDraftDisabled =
    isMutating || (!!selectedResidence && currentStatus === "draft" && !isDirty);

  // Same no-op guard for the inner room dialog: don't re-Publish an unchanged
  // already-published room, nor re-Save-draft an unchanged already-draft one.
  const editingRoom = editingRoomTypeId
    ? roomTypes.find((rt) => rt.tempId === editingRoomTypeId)
    : null;
  const isRoomDialogDirty =
    !editingRoom || serializeRoomFields(roomTypeFormData) !== serializeRoomFields(editingRoom);
  const editingRoomStatus = editingRoom?.status ?? "published";
  const isRoomDialogPublishDisabled =
    !!editingRoom && editingRoomStatus === "published" && !isRoomDialogDirty;
  const isRoomDialogDraftDisabled =
    !!editingRoom && editingRoomStatus === "draft" && !isRoomDialogDirty;
  // Has the room dialog been edited since it opened? Drives its close confirm.
  // (Unlike the button-dirty check, a brand-new untouched room is NOT "changed",
  // so closing an empty Add Room dialog won't prompt.)
  const roomDialogHasChanges =
    roomDialogInitialRef.current !== null &&
    serializeRoomFields(roomTypeFormData) !== roomDialogInitialRef.current;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search residences..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button onClick={openAddModal}>
          <Plus className="h-4 w-4 mr-2" />
          Add Residence
        </Button>
      </div>

      {/* Desktop table */}
      <Card className="hidden md:block">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Image</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredResidences.map((residence) => (
                  <TableRow key={residence.id}>
                    <TableCell>
                      <img
                        src={residence.imageUrl}
                        alt={residence.name}
                        className="w-12 h-12 object-cover rounded"
                      />
                    </TableCell>
                    <TableCell className="font-medium">{residence.name}</TableCell>
                    <TableCell>{residence.city}</TableCell>
                    <TableCell>
                      {residence.startingPrice !== null && residence.startingPrice !== undefined
                        ? `€${residence.startingPrice}/mo`
                        : "Price on request"}
                    </TableCell>
                    <TableCell>
                      {isDraft(residence)
                        ? <StatusBadge status="draft" />
                        : <StatusBadge status={residence.active ? "active" : "inactive"} />}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(`/admin/residences/${residence.id}/regulations`)}
                        title="Manage Regulations"
                      >
                        <FileText className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openEditModal(residence)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="text-destructive" onClick={() => openDeleteDialog(residence)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Mobile card list */}
      <div className="md:hidden space-y-3">
        {filteredResidences.map((residence) => (
          <Card key={residence.id}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <img
                  src={residence.imageUrl}
                  alt={residence.name}
                  className="w-14 h-14 object-cover rounded flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-medium truncate">{residence.name}</p>
                    {isDraft(residence)
                      ? <StatusBadge status="draft" className="shrink-0" />
                      : <StatusBadge status={residence.active ? "active" : "inactive"} className="shrink-0" />}
                  </div>
                  <p className="text-sm text-muted-foreground">{residence.city}</p>
                  <p className="text-sm font-medium mt-1">
                    {residence.startingPrice !== null && residence.startingPrice !== undefined
                      ? `€${residence.startingPrice}/mo`
                      : "Price on request"}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 mt-3 pt-3 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => navigate(`/admin/residences/${residence.id}/regulations`)}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Regulations
                </Button>
                <Button variant="outline" size="sm" className="flex-1" onClick={() => openEditModal(residence)}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <Button variant="ghost" size="sm" className="text-destructive" onClick={() => openDeleteDialog(residence)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Add/Edit Modal */}
      <Dialog
        open={isModalOpen}
        onOpenChange={(open) => {
          if (open) {
            setIsModalOpen(true);
            return;
          }
          // Closing via overlay click / Esc / X — confirm first if there are
          // unsaved edits, otherwise close immediately.
          if (isDirty) {
            setIsDiscardDialogOpen(true);
            return;
          }
          setIsModalOpen(false);
        }}
      >
        <DialogContent
          className="max-w-[95vw] sm:max-w-4xl max-h-[90vh] overflow-y-auto"
          onInteractOutside={(e) => {
            // Don't close when the interaction is with a portaled dropdown/combobox.
            if (clickIsInsidePopper(e.target)) e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>{selectedResidence ? "Edit Residence" : "Add Residence"}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-6 py-4">
            {/* Residence Details */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Residence Details</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name || ""}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">City *</Label>
                  <Select
                    value={formData.city || ""}
                    onValueChange={(value) => setFormData({ ...formData, city: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select city" />
                    </SelectTrigger>
                    <SelectContent>
                      {config?.cities.map((city) => (
                        <SelectItem key={city} value={city}>
                          {city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Address *</Label>
                <Input
                  id="address"
                  value={formData.address || ""}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Short Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description || ""}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  placeholder="Brief description for listing cards..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="fullDescription">Full Description</Label>
                <Textarea
                  id="fullDescription"
                  value={formData.fullDescription || ""}
                  onChange={(e) => setFormData({ ...formData, fullDescription: e.target.value })}
                  rows={5}
                  placeholder="Detailed description shown on residence detail page..."
                />
              </div>

              <ImageUploadField
                value={formData.imageUrl || ""}
                onChange={(url) => setFormData({ ...formData, imageUrl: url })}
                storagePath={`residences/${selectedResidence?.id || newResidenceId}`}
                label="Image"
                onUploadingChange={(uploading) => setIsImageUploading(uploading)}
              />

              <div className="space-y-2">
                <Label>Starting Price (€/month)</Label>
                <div className="flex items-center h-10 px-3 py-2 text-sm bg-muted rounded-md border border-border">
                  {(() => {
                    const calculatedPrice = calculateMinimumPrice(pricedPublishedRooms(roomTypes));
                    return calculatedPrice !== null ? `€${calculatedPrice}/month` : "Price on request (add rooms)";
                  })()}
                </div>
                <p className="text-xs text-muted-foreground">
                  Automatically calculated from rooms
                </p>
              </div>

              <div className="space-y-2">
                <Label>Amenities</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {config?.amenities.map((amenity, index) => (
                    <div key={`${amenity.name}-${index}`} className="flex items-center space-x-2">
                      <Checkbox
                        id={`${amenity.name}-${index}`}
                        checked={formData.amenities?.includes(amenity.name)}
                        onCheckedChange={() => toggleAmenity(amenity.name)}
                      />
                      <label htmlFor={`${amenity.name}-${index}`} className="text-sm">
                        {amenity.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="active"
                  checked={formData.active}
                  onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                />
                <Label htmlFor="active">Active</Label>
              </div>
            </div>

            {/* Common Areas Section */}
            <div className="space-y-4 border-t pt-4">
              <div>
                <h3 className="text-lg font-semibold">Common Areas</h3>
                <p className="text-sm text-muted-foreground">
                  Shared spaces (kitchen, living room, bathrooms) shown on every room of this residence
                </p>
              </div>
              <CommonAreasFields
                values={formData.commonAreas ?? []}
                onChange={(areas) => setFormData({ ...formData, commonAreas: areas })}
                storagePathPrefix={`residences/${selectedResidence?.id || newResidenceId}`}
                onUploadingChange={setIsImageUploading}
              />
            </div>

            {/* Rooms Section */}
            <div className="space-y-4 border-t pt-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold">Rooms *</h3>
                  <p className="text-sm text-muted-foreground">
                    At least one room is required
                  </p>
                </div>
                <Button type="button" onClick={() => openRoomTypeDialog()} className="flex-shrink-0">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Room
                </Button>
              </div>

              {/* Rooms List */}
              {roomTypes.length > 0 ? (
                <div className="space-y-2">
                  {roomTypes.map((rt) => (
                    <div key={rt.tempId} className="flex items-center justify-between gap-3 p-3 border rounded-lg">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium truncate min-w-0">{rt.name}</p>
                          {rt.status === "draft" && <StatusBadge status="draft" className="shrink-0" />}
                          {rt.typologyKey && (
                            <Badge variant="secondary" className="gap-1 bg-secondary/10 text-secondary shrink-0">
                              <Home className="h-3 w-3" />
                              {rt.typologyKey}
                              {rt.unitCount > 1 ? ` · ${rt.unitCount} units` : ""}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {rt.status !== "draft"
                            ? `${rt.maxOccupancy === 1 ? `€${rt.priceByOccupancy[0]}/mo` : `from €${Math.min(...rt.priceByOccupancy)}/mo`} · ${rt.maxOccupancy} ${rt.maxOccupancy === 1 ? "person" : "people"}`
                            : "Draft — not published yet"}
                        </p>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button variant="ghost" size="icon" onClick={() => openRoomTypeDialog(rt)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDeleteRoomType(rt.tempId)}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    No rooms added yet. Please add at least one room to continue.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => (isDirty ? setIsDiscardDialogOpen(true) : setIsModalOpen(false))}
              disabled={isMutating}
            >
              Cancel
            </Button>
            <Button
              variant="secondary"
              onClick={() => handleSave(false)}
              disabled={isDraftDisabled}
            >
              {savingAction === "draft" ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save draft"
              )}
            </Button>
            <Button
              onClick={() => handleSave(true)}
              disabled={isPublishDisabled}
            >
              {savingAction === "publish" ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Publish"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Residence</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedResidence?.name}"? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteResidence.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteResidence.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteResidence.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Discard unsaved changes confirmation */}
      <AlertDialog open={isDiscardDialogOpen} onOpenChange={setIsDiscardDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. If you close now they will be lost. Use Save draft
              to keep your progress.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setIsDiscardDialogOpen(false);
                setIsModalOpen(false);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Room Dialog */}
      <Dialog
        open={isRoomTypeDialogOpen}
        onOpenChange={(open) => {
          if (open) {
            setIsRoomTypeDialogOpen(true);
            return;
          }
          // Closing via overlay click / Esc / X — confirm if the room was edited.
          if (roomDialogHasChanges) {
            setIsRoomDiscardDialogOpen(true);
            return;
          }
          closeRoomTypeDialog();
        }}
      >
        <DialogContent
          className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto"
          onInteractOutside={(e) => {
            if (clickIsInsidePopper(e.target)) e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>{editingRoomTypeId ? "Edit Room" : "Add Room"}</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <RoomTypeFormFields
              data={roomTypeFormData as RoomTypeFieldsData}
              onChange={(d) => setRoomTypeFormData((prev) => ({ ...prev, ...d }))}
              roomId={editingRoomTypeId || newRoomTypeId}
              onUploadingChange={setIsImageUploading}
              // Feed the typology combobox from the in-memory list so unsaved
              // rooms in this edit session can be reused as siblings (the user
              // doesn't have to commit the residence first to reuse a new
              // typology). tempId stands in for id — saved rooms are hydrated
              // with tempId === id, and the "exclude the room being edited"
              // check uses the same tempId via the roomId prop.
              residenceRooms={roomTypes.map((rt) => ({
                id: rt.tempId,
                name: rt.name,
                description: rt.description,
                priceByOccupancy: rt.priceByOccupancy,
                maxOccupancy: rt.maxOccupancy,
                area: rt.area,
                floorPlanUrl: rt.floorPlanUrl,
                imagesUrl: rt.imagesUrl,
                minStay: rt.minStay,
                pricePerExcessKwh: rt.pricePerExcessKwh,
                typologyKey: rt.typologyKey,
              })) as unknown as RoomType[]}
              isEditing={!!editingRoomTypeId}
              unitCount={roomFormUnitCount}
              onUnitCountChange={setRoomFormUnitCount}
              minUnitCount={
                // When editing an existing typology, don't allow dropping below
                // the number of units that already have bookings (those can't be
                // deleted); simplest safe floor is 1.
                1
              }
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                roomDialogHasChanges ? setIsRoomDiscardDialogOpen(true) : closeRoomTypeDialog()
              }
            >
              Cancel
            </Button>
            <Button
              variant="secondary"
              onClick={() => handleSaveRoomType(false)}
              disabled={isRoomDialogDraftDisabled}
            >
              Save draft
            </Button>
            <Button
              onClick={() => handleSaveRoomType(true)}
              disabled={isRoomDialogPublishDisabled}
            >
              Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discard unsaved room changes confirmation */}
      <AlertDialog open={isRoomDiscardDialogOpen} onOpenChange={setIsRoomDiscardDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard room changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes to this room. If you close now they will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setIsRoomDiscardDialogOpen(false);
                closeRoomTypeDialog();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminResidences;
